import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Waves, Utensils, Sailboat, Sparkles, MapPin, Phone, ArrowRight, Star, Check } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import DemoBookingModal from "@/components/demos/DemoBookingModal";
import { DEMO_THEMES } from "@/data/demoThemes";

const TEAL = "#1E6F78";
const SAND = "#F3F7F6";
const DARK = "#173B3E";
const serif = { fontFamily: "'Playfair Display', serif" };
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const U = "https://images.unsplash.com/";
const IMG = {
  hero: U + "photo-1782113274194-1a102c318890?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80&ixlib=rb-4.1.0",
  r1: U + "photo-1586611292717-f828b167408c?crop=entropy&cs=srgb&fm=jpg&w=600&q=80&ixlib=rb-4.1.0",
  r2: U + "photo-1597221336986-7a948756cd3a?crop=entropy&cs=srgb&fm=jpg&w=600&q=80&ixlib=rb-4.1.0",
  r3: U + "photo-1582533568805-78a15dcb01b5?crop=entropy&cs=srgb&fm=jpg&w=600&q=80&ixlib=rb-4.1.0",
};
const ROOMS = [
  { img: IMG.r1, name: "Coastal Deluxe", price: "$320", d: "Garden or partial sea view, king bed." },
  { img: IMG.r2, name: "Cliffside Suite", price: "$520", d: "Private terrace, panoramic sea views." },
  { img: IMG.r3, name: "Solara Villa", price: "$890", d: "Private plunge pool, two bedrooms, butler." },
];
const EXP = [
  { icon: Sailboat, t: "Sunset Sailing", d: "Glide along the coast as the sky turns gold." },
  { icon: Sparkles, t: "Coastal Spa Rituals", d: "Sea-salt and citrus treatments by the water." },
  { icon: Utensils, t: "Seaside Chef's Table", d: "A tasting menu with your toes near the sand." },
];
const PERKS = ["Best available rate", "Complimentary breakfast", "Flexible cancellation", "Room upgrade when available"];

export default function SolaraCoveDemo() {
  const [book, setBook] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); document.title = "Solara Cove Resort (Demo) | WebsiteTech.pro"; }, []);
  const selects = [
    { key: "room", label: "Room", options: ["Coastal Deluxe", "Cliffside Suite", "Solara Villa"] },
    { key: "guests", label: "Guests", options: ["1 Guest", "2 Guests", "3 Guests", "4+ Guests"] },
  ];

  return (
    <div className="min-h-screen" style={{ background: SAND, color: DARK, ...body }} data-testid="hotel-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5 text-white" style={{ background: TEAL }}>WebsiteTech.pro Demo · Hotel & Resort — view all demos</Link>
      <DemoTopNav testid="hotel-nav"
        logo={<div className="leading-tight"><div className="text-lg tracking-[0.2em]" style={{ ...serif, color: DARK }}>SOLARA COVE</div><div className="text-[9px] tracking-[0.35em] uppercase" style={{ color: TEAL }}>Seaside Resort</div></div>}
        items={["Rooms & Suites", "Experiences", "Dining", "Spa", "Our Story"]} ctaLabel="Book Now" onCta={() => setBook(true)}
        theme={{ bg: "rgba(243,247,246,0.92)", text: DARK, accent: TEAL, accentText: "#fff", border: "#dbe7e5" }} />

      <header data-testid="hotel-hero" className="relative">
        <img src={IMG.hero} alt="" className="h-[520px] w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(9,40,42,0.72), rgba(9,40,42,0.15))" }} />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full text-white">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-xl">
              <span className="text-xs tracking-[0.3em] uppercase" style={{ color: "#E9D9A8" }}>A Mediterranean Escape</span>
              <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={serif}>Where the coast<br />slows time.</h1>
              <p className="mt-5 text-lg opacity-90 max-w-md">Sun-warmed days, sea breezes, and quiet luxury on the water's edge.</p>
              <button onClick={() => setBook(true)} data-testid="hotel-book-hero" className="mt-7 rounded-full px-8 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white flex items-center gap-2" style={{ background: TEAL }}>Check Availability <ArrowRight size={14} /></button>
            </motion.div>
          </div>
        </div>
        {/* booking bar */}
        <div className="max-w-5xl mx-auto px-6 -mt-10 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl border p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center" style={{ borderColor: "#dbe7e5" }} data-testid="hotel-booking-bar">
            <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Check-in</div><div className="font-medium">Select date</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Check-out</div><div className="font-medium">Select date</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Guests</div><div className="font-medium">2 Guests</div></div>
            </div>
            <button onClick={() => setBook(true)} data-testid="hotel-check-availability" className="rounded-full px-8 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white whitespace-nowrap" style={{ background: TEAL }}>Search Availability</button>
          </div>
        </div>
      </header>

      <section data-testid="hotel-rooms" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-10"><span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: TEAL }}>Rooms & Suites</span><h2 className="mt-2 text-3xl" style={serif}>Rest, with a view</h2></div>
        <div className="grid md:grid-cols-3 gap-6">
          {ROOMS.map((r) => (
            <div key={r.name} className="rounded-2xl overflow-hidden bg-white border" style={{ borderColor: "#dbe7e5" }} data-testid="hotel-room">
              <img src={r.img} alt={r.name} className="h-52 w-full object-cover" />
              <div className="p-5">
                <div className="flex items-center justify-between"><h3 className="text-xl" style={serif}>{r.name}</h3><span className="text-sm" style={{ color: TEAL }}>from {r.price}/night</span></div>
                <p className="text-sm opacity-70 mt-1">{r.d}</p>
                <button onClick={() => setBook(true)} data-testid="hotel-room-book" className="mt-4 w-full rounded-full py-2.5 text-xs tracking-widest uppercase font-semibold text-white" style={{ background: TEAL }}>Book Now</button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-2xl p-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3" style={{ background: "#E7F0EF" }}>
          <span className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: TEAL }}>Book Direct & Enjoy</span>
          {PERKS.map((p) => <span key={p} className="flex items-center gap-1.5 text-sm"><Check size={15} style={{ color: TEAL }} /> {p}</span>)}
        </div>
      </section>

      <section data-testid="hotel-experiences" className="py-20" style={{ background: "#E7F0EF" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10"><span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: TEAL }}>Signature Experiences</span><h2 className="mt-2 text-3xl" style={serif}>Days worth remembering</h2></div>
          <div className="grid md:grid-cols-3 gap-6">
            {EXP.map((e) => (
              <div key={e.t} className="bg-white rounded-2xl p-6 border" style={{ borderColor: "#dbe7e5" }}>
                <e.icon size={26} style={{ color: TEAL }} /><h3 className="mt-3 text-lg" style={serif}>{e.t}</h3><p className="text-sm opacity-70 mt-1">{e.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <div className="flex justify-center gap-1 mb-3" style={{ color: "#E0B84C" }}>{[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}</div>
        <p className="text-2xl md:text-3xl leading-relaxed" style={serif}>"The most restful week we've had in years. Every detail felt considered, every sunset unforgettable."</p>
        <div className="mt-4 text-sm opacity-60 flex items-center justify-center gap-2"><Waves size={14} /> The Alvarez Family</div>
      </section>

      <footer data-testid="hotel-footer" className="text-white" style={{ background: DARK }}>
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div style={serif} className="tracking-[0.2em] text-lg">SOLARA COVE</div>
          <span className="flex items-center gap-4 opacity-80"><span className="flex items-center gap-1.5"><MapPin size={13} /> The Mediterranean Coast</span><span className="flex items-center gap-1.5"><Phone size={13} /> +1 (555) 700-2200</span></span>
          <p className="opacity-70 italic" style={serif}>Where the coast slows time.</p>
        </div>
      </footer>

      {book && <DemoBookingModal demo="hotel" title="Reserve your stay" accent={TEAL} submitLabel="Request reservation" selects={selects} onClose={() => setBook(false)} />}
      <AIConciergeWidget demo="hotel" concierge={DEMO_THEMES.hotel.concierge} />
    </div>
  );
}
